/** Market table (screens §F1): the latest released market with the private supervisory column and each seat's round status, on the one table system (units in the headers, whole numbers, flat headers). */
import type { BoardTeamRow, MarketView, RoundSummary } from "@bsim/contracts";
import { displayRank, formatKpi } from "../format";
import { figure, type FigureUnit } from "../theme/figures";
import { GxNotes } from "../theme/GxNotes";
import { GxPill } from "../theme/GxPill";
import { GxTable, type GxColumn, type GxRow } from "../theme/GxTable";
import { useBand } from "../theme/breakpoints";
import { RANK_RULE } from "@gxs/metric-defs";
import { GATE_RANK_REASON } from "../score/labels";
import { cmTone } from "./TeamCard";
import { PIPELINE_WORDS, aiReady } from "./derive";
import { roundDone, stageWords } from "./words";

/** A KPI level as a bare figure for a column whose header carries the unit; a rate is a decimal, shown in per cent. */
const level = (bank: MarketView["banks"][number], id: "price" | "ptbv" | "rotce" | "nim" | "cet1Reported"): string => {
  const v = bank.kpis[id];
  if (v === undefined) return "—";
  return id === "price" ? figure(v, "usdPerShare") : id === "ptbv" ? figure(v, "x") : figure(v * 100, id === "rotce" ? "pct" : "pct2");
};
const rate = (v: number | null | undefined, unit: FigureUnit): string => (v === null || v === undefined ? "—" : figure(v * 100, unit));

const kpi = (bank: MarketView["banks"][number], id: "price" | "ptbv" | "rotce" | "nim" | "cet1Reported"): string => {
  const v = bank.kpis[id];
  return v === undefined ? "–" : formatKpi(id, v);
};

export function MarketTable({ market, rows, round, title, note }: { readonly market: MarketView; readonly rows: readonly BoardTeamRow[]; readonly round: RoundSummary | null; readonly title: string; readonly note: string }) {
  // A phone has no room for twelve columns: each bank is a block (bank and rank on a line, the twin gap beside it, then one line a measure), not a sideways scroller.
  const phone = useBand() === "phone";
  const banks = [...market.banks].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  // Until a round is released the banks are the same bank: one line says so instead of six identical rows.
  const first = banks[0];
  const identical = market.throughRoundNo === 0 && first !== undefined && banks.every((b) => (["price", "ptbv", "rotce", "nim", "cet1Reported"] as const).every((k) => b.kpis[k] === first.kpis[k]));
  if (identical) return <section className="gx-card gx-fac-market" data-testid="board.market"><div className="gx-card-h"><h2 className="gx-h2">{title}</h2></div><p className="gx-card-b gx-muted" data-testid="market.identical">All banks start identical, at {kpi(first, "price")} a share.</p></section>;
  // Once the round is done every row would say "Complete": the column goes, and the card's header already says the round is over.
  const showStatus = !roundDone(round);
  const columns: readonly GxColumn[] = [
    ...(phone ? [] : [{ key: "rank", label: "#", width: "3rem" }]),
    { key: "bank", label: "Bank" },
    { key: "twin", label: "Vs twin", kind: "number", unit: "usdPerShare", ...(phone ? { phone: "lead" as const } : {}) },
    { key: "points", label: "Points", kind: "number", unit: "pts" },
    { key: "price", label: "Price", kind: "number", unit: "usdPerShare" },
    { key: "ptbv", label: "P/TBV", kind: "number", unit: "x" },
    { key: "rotce", label: "ROTCE", kind: "number", unit: "pct" },
    { key: "nim", label: "NIM", kind: "number", unit: "pct2" },
    { key: "cet1", label: "Reported CET1", kind: "number", unit: "pct2" },
    { key: "cet1m", label: "Marked CET1", kind: "number", unit: "pct2" },
    { key: "slc", label: "Stress coverage", kind: "number", unit: "pct" },
    { key: "cm", label: "Rating", kind: "number" },
    ...(showStatus ? [{ key: "status", label: round === null ? "Round status" : `Round ${round.roundNo} status` }] : []),
  ];
  const tableRows: readonly GxRow[] = banks.map((b) => {
    const row = rows.find((r) => r.teamId === b.teamId);
    const score = b.score;
    return {
      key: b.teamId, testId: `market.${b.teamId}`,
      cells: {
        ...(phone ? {} : { rank: <b className="gx-faint">{displayRank(b)}</b> }),
        bank: <span><b className="gx-faint gx-fac-rankin">{displayRank(b)} </b>{b.name}{row?.isAi === true ? <> <GxPill tone="info">AI</GxPill></> : null}{b.gateRankedLast === true ? <> <GxPill tone="warn" title={GATE_RANK_REASON} testId={`market.gate.${b.teamId}`}>Rated 3 or worse</GxPill></> : null}</span>,
        twin: score === null ? "—" : <b className={score.valueCreatedUsdPerShare >= 0 ? "gx-fac-up" : "gx-fac-dn"}>{figure(score.valueCreatedUsdPerShare, "usdPerShare", { signed: true })}</b>,
        points: score === null ? "—" : <span className="gx-faint">{figure(score.valueCreatedRate * 100, "pts", { signed: true })}</span>,
        price: level(b, "price"), ptbv: level(b, "ptbv"), rotce: level(b, "rotce"), nim: level(b, "nim"), cet1: level(b, "cet1Reported"),
        cet1m: rate(row?.cet1Marked, "pct2"),
        slc: rate(row?.liquidityStressCoverage ?? row?.stressCoverage, "pct"),
        cm: row?.camelsComposite == null ? "—" : <GxPill tone={cmTone(row.camelsComposite)}>{row.camelsComposite}</GxPill>,
        status: <span className="gx-faint">{row === undefined ? "" : row.isAi ? (aiReady(row, round) ? "Ready" : "Waiting") : row.pipelineStage === null ? "" : stageWords(row.pipelineStage, round, PIPELINE_WORDS)}</span>,
      },
    };
  });
  return (
    <section className="gx-card gx-fac-market" data-testid="board.market">
      <div className="gx-card-h"><h2 className="gx-h2">{title}</h2><span className="gx-small gx-faint">{note}</span></div>
      <div className="gx-fac-market-b">
        <GxTable caption={title} columns={columns} rows={tableRows} dense phone={phone ? "stack" : "grid"} />
        <GxNotes notes={[banks.some((b) => b.gateRankedLast === true) ? RANK_RULE : null]} />
      </div>
    </section>
  );
}
